import { useRef, useState } from 'react'
import { Avatar } from './Avatar.tsx'
import { EntityCard } from './EntityCard.tsx'
import { MessageFiles } from './MessageFiles.tsx'
import { PollCard } from './PollCard.tsx'
import { messageTime, whenItGoes } from '../lib/format.ts'
import { RichText } from '../lib/richText.tsx'
import type { Message } from '../api/types.ts'

type Props = {
  message: Message
  /** Показывать аватар и имя: только у первого сообщения в серии */
  showAuthor: boolean
  isGroup: boolean
  onMenu: (message: Message, at: { x: number; y: number }) => void
  onReaction: (messageId: string, emoji: string) => void
  /** Прочитали ли собеседники — по этому рисуются галочки */
  readByOthers: boolean
  onJumpTo: (messageId: string) => void
  onReply: (message: Message) => void
  /** Идёт выбор нескольких: нажатие отмечает, а не открывает */
  selecting: boolean
  isSelected: boolean
  onToggleSelect: () => void
}

export function MessageItem({ message, showAuthor, isGroup, onMenu, onReaction, readByOthers, onJumpTo, onReply, selecting, isSelected, onToggleSelect }: Props) {
  const longPress = useRef<number | undefined>(undefined)

  /**
   * Свайп для ответа — привычный жест на телефоне.
   *
   * Тянем только вправо и только пальцем: мышью для этого есть меню.
   * Порог в 56 пикселей выбран так, чтобы жест не срабатывал при
   * обычной прокрутке, когда палец слегка уходит вбок.
   */
  const [shift, setShift] = useState(0)
  const swipeFrom = useRef<{ x: number; y: number } | null>(null)

  /**
   * На телефоне контекстного меню нет, поэтому ловим долгое нажатие.
   * Полсекунды — привычный порог: короче срабатывает при обычном касании
   * во время прокрутки, длиннее воспринимается как «не отреагировало».
   */
  const startPress = (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse' || message.deleted || message.isSystem) return
    const { clientX: x, clientY: y } = event
    swipeFrom.current = { x, y }
    longPress.current = window.setTimeout(() => onMenu(message, { x, y }), 500)
  }

  const movePress = (event: React.PointerEvent) => {
    const from = swipeFrom.current
    if (!from) return

    const dx = event.clientX - from.x
    const dy = event.clientY - from.y

    // Движение по вертикали — это прокрутка, не жест: отпускаем сообщение
    if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
      window.clearTimeout(longPress.current)
      swipeFrom.current = null
      setShift(0)
      return
    }

    if (dx > 6) {
      window.clearTimeout(longPress.current)
      // Сопротивление на конце: дальше 72 пикселей тянется всё туже,
      // и понятно, что предел достигнут
      setShift(Math.min(dx, 72 + (dx - 72) * 0.2))
    }
  }

  const endPress = () => {
    window.clearTimeout(longPress.current)
    if (shift >= 56 && !message.deleted && !message.isSystem) onReply(message)
    swipeFrom.current = null
    setShift(0)
  }

  const cancelPress = () => {
    window.clearTimeout(longPress.current)
    swipeFrom.current = null
    setShift(0)
  }
  /**
   * Подпись сообщения: время, пометки и галочки.
   *
   * Собирается один раз и ставится дважды — видимой в углу и невидимой
   * распоркой в конце текста. Раньше распорка перечисляла содержимое
   * подписи сама, по памяти: «время, а если правили — ещё и „изменено“».
   * Стоило добавить в подпись что-то ещё, как распорка оказывалась уже
   * подписи, и последняя строка наезжала на время.
   */
  const own = message.mine

  const meta = (
    <>
          {/* Отложенное до срока видит только тот, кто его написал.
              Без пометки оно выглядит как уже отправленное — и человек
              вторым заходом пишет то же самое ещё раз */}
          {message.scheduledAt && new Date(message.scheduledAt) > new Date() && (
            <span className="bubble-scheduled">
              уйдёт {whenItGoes(message.scheduledAt)}
            </span>
          )}
          {message.editedAt && <span className="bubble-edited">изменено</span>}
          <span className="bubble-time">{messageTime(message.createdAt)}</span>
          {/**
           * Часики вместо галочек, пока сообщение не ушло: человек
           * должен видеть разницу между «отправлено» и «ждёт связи».
           * Красный восклицательный знак — не ушло и само уже не уйдёт.
           */}
          {message.pending ? (
            <span
              className={'ticks is-pending' + (message.failed ? ' is-failed' : '')}
              aria-label={message.failed ? 'не отправлено' : 'ждёт связи'}
            >
              {message.failed ? (
                '!'
              ) : (
                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor"
                     strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="8" r="6.5" />
                  <path d="M8 4.5V8l2.5 1.5" />
                </svg>
              )}
            </span>
          ) : (
            own &&
            !message.deleted && (
              <span className={'ticks' + (readByOthers ? ' is-read' : '')} aria-label={readByOthers ? 'прочитано' : 'доставлено'}>
                <svg viewBox="0 0 18 12" width="16" height="11" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M1 6.5 4.5 10 11 2.5" strokeLinecap="round" strokeLinejoin="round" />
                  {readByOthers && <path d="M7 9.5 8 10.6 15.5 2" strokeLinecap="round" strokeLinejoin="round" />}
                </svg>
              </span>
            )
          )}
    </>
  )

  if (message.isSystem) {
    return (
      <div className="system-message">
        <span>{message.body}</span>
      </div>
    )
  }

  const hasText = Boolean(message.body)

  return (
    <div
      className={
        'msg-row' +
        (own ? ' is-own' : '') +
        (showAuthor ? ' has-author' : '') +
        (selecting ? ' is-selecting' : '') +
        (isSelected ? ' is-selected' : '')
      }
      onClick={selecting ? onToggleSelect : undefined}
      /**
       * Двойное нажатие ставит сердце — как в привычных мессенджерах.
       *
       * Это самая частая реакция, и лезть ради неё в меню долго:
       * пока открыл, выбрал и закрыл, желание поставить уже прошло.
       * Повторное двойное нажатие её снимает — тем же путём, что
       * и появилась.
       */
      onDoubleClick={() => {
        if (selecting || message.deleted || message.pending) return
        onReaction(message.id, '❤️')
      }}
    >
      {selecting && (
        <span className="select-mark" aria-hidden="true">
          {isSelected ? '✓' : ''}
        </span>
      )}
      {shift > 0 && (
        <span className="swipe-hint" style={{ opacity: Math.min(shift / 56, 1) }} aria-hidden="true">
          ↩
        </span>
      )}
      {!own && isGroup && (
        <div className="msg-avatar">
          {showAuthor && message.author && (
            <Avatar id={message.author.id} name={message.author.displayName} url={message.author.avatarUrl} size={34} />
          )}
        </div>
      )}

      <div
        className={
          'bubble' +
          (own ? ' is-own' : '') +
          (message.deleted ? ' is-deleted' : '') +
          // Когда текста нет, подписи времени не за что зацепиться:
          // распорка живёт внутри текста, а его тут просто нет.
          // Освобождаем место снизу, иначе время ложится на содержимое.
          (!hasText && !message.deleted ? ' is-media-only' : '')
        }
        onContextMenu={(event) => {
          if (message.deleted || message.isSystem) return
          event.preventDefault()
          onMenu(message, { x: event.clientX, y: event.clientY })
        }}
        onPointerDown={startPress}
        onPointerUp={endPress}
        onPointerMove={movePress}
        onPointerCancel={cancelPress}
        style={
          shift > 0
            ? { transform: `translateX(${shift}px)`, transition: 'none' }
            : { transition: 'transform 0.18s' }
        }
      >
        {showAuthor && !own && isGroup && message.author && (
          <div className="bubble-author">{message.author.displayName}</div>
        )}

        {message.forwardedFrom && (
          <div className="forwarded">Переслано от {message.forwardedFrom}</div>
        )}

        {message.replyTo && (
          <button
            className={'bubble-reply' + (message.replyTo.deleted ? ' is-gone' : '')}
            onClick={() => {
              // К удалённому переходить некуда: его больше нет в ленте
              if (message.replyTo && !message.replyTo.deleted) onJumpTo(message.replyTo.id)
            }}
            title={message.replyTo.deleted ? 'Сообщения больше нет' : 'Перейти к сообщению'}
          >
            <div className="bubble-reply-author">{message.replyTo.authorName ?? 'Сообщение'}</div>
            <div className="bubble-reply-text">
              {message.replyTo.deleted
                ? 'сообщение удалено'
                : (message.replyTo.body ?? 'вложение')}
            </div>
          </button>
        )}

        {message.deleted ? (
          <div className="bubble-text is-muted">Сообщение удалено</div>
        ) : (
          <>
            {message.poll && <PollCard poll={message.poll} own={own} />}
            {message.files.length > 0 && <MessageFiles files={message.files} own={own} />}
            {message.cards.map((card) => (
              <EntityCard key={card.entityType + card.entityId} card={card} />
            ))}
            {hasText && (
              <div className="bubble-text">
                <RichText text={message.body ?? ''} />
                {/* Невидимая распорка ровно под подпись: текст обтекает её
                    и не залезает под время. Фиксированного отступа не хватает —
                    с пометкой «изменено» подпись заметно шире. */}
                <span className="bubble-meta-space" aria-hidden="true">
                  {meta}
                </span>
              </div>
            )}
          </>
        )}

        <div className="bubble-meta">{meta}</div>

        {/**
         * Полоска поверх отправляемого.
         *
         * Кольцо телеграма здесь ни к чему: вложений в сообщении бывает
         * несколько, и колец было бы несколько. Одна полоска на всё
         * сообщение говорит то же самое и не спорит с содержимым.
         */}
        {message.uploadPercent !== undefined && message.uploadPercent < 100 && (
          <div
            className="upload-bar"
            role="progressbar"
            aria-valuenow={message.uploadPercent}
            aria-label="Отправляется"
          >
            <span style={{ width: `${message.uploadPercent}%` }} />
          </div>
        )}

        {message.reactions.length > 0 && (
          <div className="bubble-reactions">
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                className={'reaction' + (r.mine ? ' is-mine' : '')}
                onClick={() => onReaction(message.id, r.emoji)}
                title={r.mine ? 'Убрать свою' : 'Поддержать'}
              >
                {r.emoji} {r.count}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
